import { jsx as _jsx } from "react/jsx-runtime";
import { isEqual } from "lodash-es";
import { createContext, useContext, useEffect, useState } from "react";
import { create } from "zustand";
const createSingleton = (creator) => {
    const lazySingletonRef = { current: null };
    const mountedRef = { current: 0 };
    const mountList = [];
    const mount = (m) => {
        mountList.push(m);
    };
    const unmountList = [];
    const _init = () => {
        if (mountedRef.current === 0) {
            mountList.forEach((m) => {
                unmountList.push(m());
            });
            mountList.length = 0;
        }
        mountedRef.current++;
        return () => {
            mountedRef.current--;
            Promise.resolve().then(() => {
                if (mountedRef.current === 0) {
                    unmountList.forEach((un) => un?.());
                    unmountList.length = 0;
                    lazySingletonRef.current = null;
                }
            });
        };
    };
    const getSingleton = () => {
        if (!lazySingletonRef.current) {
            lazySingletonRef.current = creator({ mount });
            return lazySingletonRef.current;
        }
        else {
            return lazySingletonRef.current;
        }
    };
    return { getSingleton, _init };
};
export const leitenSingleton = (creator) => {
    const singleton = createSingleton(creator);
    const useSingleton = () => {
        useEffect(singleton._init, []);
        return singleton.getSingleton();
    };
    return { ...singleton, useSingleton };
};
export function leitenFeature(staticCreator, creator) {
    const storeCreator = (creator || staticCreator);
    const _static = creator ? leitenSingleton(staticCreator) : undefined;
    const StoreContext = createContext(null);
    const useFeature = () => {
        const store = useContext(StoreContext);
        if (!store) {
            throw new Error("[leitenFeature] The provider is not defined");
        }
        return store;
    };
    const UnitProvider = ({ children, value, }) => {
        useEffect(value._init, []);
        return _jsx(StoreContext.Provider, { value: value, children: children });
    };
    const _creator = (useProps) => {
        const mountList = [];
        const mount = (m) => mountList.push(m);
        const _init = () => {
            const unmountList = mountList.map((m) => m());
            return () => {
                unmountList.forEach((un) => un?.());
            };
        };
        if (_static) {
            mount(_static._init);
        }
        return Object.assign(storeCreator({ useProps, mount }, _static?.getSingleton()), {
            _init,
        });
    };
    const StoreProvider = ({ children, value, }) => {
        const usePropsStore = useProps(value);
        const [store] = useState(() => _creator(usePropsStore));
        useEffect(store._init, []);
        return _jsx(StoreContext.Provider, { value: store, children: children });
    };
    const Provider = Object.assign(StoreProvider, { Unit: UnitProvider });
    const useHook = Object.assign(useFeature, { create: _creator });
    return (_static ? [useHook, Provider, _static.useSingleton] : [useHook, Provider]);
}
const useProps = (props) => {
    const [usePropsStore] = useState(() => create(() => props));
    useEffect(() => {
        if (!isEqual(props, usePropsStore.getState()))
            usePropsStore.setState(props);
    }, [props]);
    return usePropsStore;
};
